import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Campaign } from 'Modules/Core/EmailCampaign/EmailCampaignModels';
import { AxiosResponse } from 'axios';
import httpAdapterInstance from 'configs/HttpAdapterConfig';
import { EmployerApiEndpoints } from 'shared/ApiEndpoints';

interface EmailCampaignsState {
  fetchCampaignsStatus: 'idle' | 'pending' | 'success' | 'failed';
  fetchCampaignsResponse: string;
  createCampaignStatus: 'idle' | 'pending' | 'success' | 'failed';
  createCampaignResponse: string;
  campaigns: Campaign[];
}

const initialState: EmailCampaignsState = {
  fetchCampaignsStatus: 'idle',
  fetchCampaignsResponse: '',
  createCampaignStatus: 'idle',
  createCampaignResponse: '',
  campaigns: [],
};

// Fetch all campaigns for a job
export const fetchEmailCampaignsByJob = createAsyncThunk<Campaign[], { jobId: string | number }, { rejectValue: string }>(
  'emailCampaigns/fetchByJob',
  ({ jobId }, { rejectWithValue }) => {
    return httpAdapterInstance
      .get(`${EmployerApiEndpoints.EMAIL_CAMPAIGNS}/jobs/${jobId}/campaigns`)
      .then((res: AxiosResponse<Campaign[]>) => res.data)
      .catch((err: any) => rejectWithValue(err?.response?.data?.error || 'Failed to fetch campaigns'));
  }
);


// Create a new campaign for a job
export const createEmailCampaign = createAsyncThunk<Campaign, { jobId: string | number; data: Partial<Campaign> }, { rejectValue: string }>(
  'emailCampaigns/create',
  ({ jobId, data }, { rejectWithValue }) => {
    return httpAdapterInstance
      .post(`${EmployerApiEndpoints.EMAIL_CAMPAIGNS}/jobs/${jobId}/campaigns`, data)
      .then((res: AxiosResponse<Campaign>) => res.data)
      .catch((err: any) => rejectWithValue(err?.response?.data?.error || 'Failed to create campaign'));
  }
);

// Delete a campaign by id
export const deleteEmailCampaign = createAsyncThunk<number, { id: number }, { rejectValue: string }>(
  'emailCampaigns/delete',
  ({ id }, { rejectWithValue }) => {
    return httpAdapterInstance
      .delete(`${EmployerApiEndpoints.EMAIL_CAMPAIGNS}/campaigns/${id}`)
      .then(() => id)
      .catch((err: any) => rejectWithValue(err?.response?.data?.error || 'Failed to delete campaign'));
  }
);

// Update a campaign by id
export const updateEmailCampaign = createAsyncThunk<Campaign, { id: number, data: Partial<Campaign> }, { rejectValue: string }>(
  'emailCampaigns/update',
  ({ id, data }, { rejectWithValue }) => {
    return httpAdapterInstance
      .put(`${EmployerApiEndpoints.EMAIL_CAMPAIGNS}/campaigns/${id}`, data)
      .then((res: AxiosResponse<Campaign>) => res.data)
      .catch((err: any) => rejectWithValue(err?.response?.data?.error || 'Failed to update campaign'));
  }
);

// Send a campaign by id
export const sendEmailCampaign = createAsyncThunk<Campaign, { id: number }, { rejectValue: string }>(
  'emailCampaigns/send',
  ({ id }, { rejectWithValue }) => {
    return httpAdapterInstance
      .post(`${EmployerApiEndpoints.EMAIL_CAMPAIGNS}/campaigns/${id}/send`)
      .then((res: AxiosResponse<Campaign>) => res.data)
      .catch((err: any) => rejectWithValue(err?.response?.data?.error || 'Failed to send campaign'));
  }
);

const emailCampaignsSlice = createSlice({
  name: 'emailCampaigns',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmailCampaignsByJob.pending, (state) => {
        state.fetchCampaignsStatus = 'pending';
        state.fetchCampaignsResponse = '';
      })
      .addCase(fetchEmailCampaignsByJob.fulfilled, (state, action: PayloadAction<Campaign[]>) => {
        state.fetchCampaignsStatus = 'success';
        state.campaigns = action.payload;
      })
      .addCase(fetchEmailCampaignsByJob.rejected, (state, action) => {
        state.fetchCampaignsStatus = 'failed';
        state.fetchCampaignsResponse = action.payload || 'Failed to fetch campaigns';
      })
      .addCase(createEmailCampaign.pending, (state) => {
        state.createCampaignStatus = 'pending';
        state.createCampaignResponse = '';
      })
      .addCase(createEmailCampaign.fulfilled, (state, action: PayloadAction<Campaign>) => {
        state.createCampaignStatus = 'success';
        state.campaigns.unshift(action.payload);
      })
      .addCase(createEmailCampaign.rejected, (state, action) => {
        state.createCampaignStatus = 'failed';
        state.createCampaignResponse = action.payload || 'Failed to create campaign';
      })
      .addCase(deleteEmailCampaign.fulfilled, (state, action: PayloadAction<number>) => {
        state.campaigns = state.campaigns.filter(c => c.id !== action.payload);
      })
      .addCase(updateEmailCampaign.fulfilled, (state, action: PayloadAction<Campaign>) => {
        const idx = state.campaigns.findIndex(c => c.id === action.payload.id);
        if (idx !== -1) {
          state.campaigns[idx] = action.payload;
        }
      })
      .addCase(sendEmailCampaign.fulfilled, (state, action: PayloadAction<Campaign>) => {
        const idx = state.campaigns.findIndex(c => c.id === action.payload.id);
        if (idx !== -1) {
          state.campaigns[idx] = action.payload;
        }
      });
  },
});

export const { reducer: emailCampaignsReducer } = emailCampaignsSlice;
export default emailCampaignsReducer; 